import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
    ArrowLeft, ArrowRight, Sparkles, Film, Tv, Tv2,
    Palette, Wand2, Video, Scissors, Users, ShieldCheck,
    FileText, CheckCircle2, Clock,
} from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useBeta } from "../context/BetaContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";

// iter-49: production type drives the script-setup workflow downstream.
const PRODUCTION_TYPES = [
    { key: "feature_film", label: "Feature Film",
      description: "One script. One unified scene list.", Icon: Film },
    { key: "tv_single", label: "Single TV Episode",
      description: "One script for a one-off TV piece or pilot.", Icon: Tv },
    { key: "tv_series", label: "Episodic TV Series",
      description: "One script per episode — Scenes group by episode.", Icon: Tv2 },
];

// Iter-158.13.2.20e.11 — Where will you be working? Presentation-only
// destination choice. This does NOT create or alter membership. The
// creator remains Production Owner and (per the existing pre-seed)
// H&M Admin; the chosen destination controls only the final landing
// URL and the human framing of the setup journey.
const WORKING_DESTINATIONS = [
    { key: "hair_makeup", label: "Hair & Makeup",
      description: "Lead your Hair & Makeup department.", Icon: Wand2,
      landingFor: (pid) => `/beta/hm-app/projects/${pid}` },
    { key: "production", label: "Production",
      description: "Work at Production level (Script Supervisor, Producer, Continuity).",
      Icon: Users,
      landingFor: (pid) => `/beta/projects/${pid}` },
    { key: "art", label: "Art",
      description: "Set / props / production design.", Icon: Palette,
      landingFor: (pid) => `/beta/projects/${pid}/departments/art` },
    { key: "costume", label: "Costume",
      description: "Wardrobe & continuity.", Icon: Scissors,
      landingFor: (pid) => `/beta/projects/${pid}/departments/costume` },
    { key: "vfx_ai", label: "VFX & AI",
      description: "Visual effects, plates, AI references.", Icon: Video,
      landingFor: (pid) => `/beta/projects/${pid}/departments/vfx_ai` },
];

export default function CreateProject() {
    const navigate = useNavigate();
    const { hasBetaAccess } = useBeta();

    // Iter-158.13.2.20e.11 — mini setup wizard for beta users.
    // Steps mirror the target creator journey:
    //   1. Name + Production type            → creates production + project
    //   2. Where will you be working?         → chosen destination (presentation)
    //   3. Setup checklist                    → links out to canonical Script
    //                                          Setup and Production Storage;
    //                                          finish → chosen destination.
    // Non-beta users stay on the single-step legacy path.
    const [step, setStep] = useState(1);
    const [name, setName] = useState("");
    // Iter-158.13.2.20f.1 (#3) — no silent default. Force explicit
    // selection so Continue stays disabled until the producer picks
    // a production type.
    const [productionType, setProductionType] = useState("");
    const [destinationKey, setDestinationKey] = useState("hair_makeup");
    const [createdProjectId, setCreatedProjectId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    // Iter-158.13.2.20f.3 #10 — controlled Production creation gate.
    // Every visitor first probes their service-level entitlement.
    // Users on the operator allowlist / previously approved skip the
    // gate; everyone else lands on the request form (with a pending
    // status card once submitted) instead of the wizard. Server-side
    // enforced — the FE gate is a UX convenience, not the security.
    const [accessStatus, setAccessStatus] = useState(null); // null | { can_create, request }
    const [accessLoading, setAccessLoading] = useState(true);
    const [accessSubmitting, setAccessSubmitting] = useState(false);
    const [accessError, setAccessError] = useState("");
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/production-access/status");
                if (!cancelled) setAccessStatus(data || { can_create: true, request: null });
            } catch {
                // No entitlement endpoint / error → assume gated so
                // the operator's server-side gate remains the SOLE
                // source of truth. Users see a calm request form.
                if (!cancelled) setAccessStatus({ can_create: false, request: null });
            } finally {
                if (!cancelled) setAccessLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    // Iter-158.13.2.20f.7 (BLOCKER 1) — per-Production request.
    // The wizard now runs IN FRONT of the access gate: users configure
    // the specific Production they want (name + type + destination)
    // BEFORE any authorisation check happens. On the final Continue
    // we either create the Production directly (grandfathered /
    // allowlisted callers) or submit a `production-access/request`
    // carrying the wizard payload so approval creates THAT exact
    // Production — no state loss, no unrelated Production created,
    // no global entitlement grant.
    //
    // Iter-158.13.2.20f.7.1 (UAT correction) — fail-safe error copy.
    // The backend returns 403 with `detail="production_creation_pending_approval"`
    // when a gated user hits `/api/projects` or `/api/beta/productions`
    // directly. We must NEVER surface that raw enum as user copy —
    // convert it into a plain retry prompt. Any other error uses the
    // generic apiError formatter unchanged.
    const humaniseAccessError = (err) => {
        const raw = formatApiError(err) || "";
        if (/production_creation_pending_approval/i.test(raw)) {
            return "We couldn't submit your Production request. Please try again.";
        }
        return raw || "We couldn't submit your Production request. Please try again.";
    };

    const submitAccessRequest = async () => {
        setAccessError("");
        setAccessSubmitting(true);
        try {
            const { data } = await api.post("/production-access/request", {
                requested_production: {
                    name: name.trim(),
                    production_type: productionType,
                    destination_key: destinationKey,
                },
            });
            // Only advance the lifecycle view if the server actually
            // persisted a pending row — otherwise fall through into
            // the failure branch so we never claim "sent" without a
            // real persisted request.
            if (data && data.request && data.request.id && data.request.status === "pending") {
                setAccessStatus(data);
                return true;
            }
            setAccessError("We couldn't submit your Production request. Please try again.");
            return false;
        } catch (err) {
            setAccessError(humaniseAccessError(err));
            return false;
        } finally {
            setAccessSubmitting(false);
        }
    };

    const chosenDestination = WORKING_DESTINATIONS.find(
        (d) => d.key === destinationKey,
    ) || WORKING_DESTINATIONS[0];
    const finalLanding = createdProjectId
        ? chosenDestination.landingFor(createdProjectId)
        : null;

    // Iter-158.13.2.20f.8 — Stale Approval Intercept Defect.
    // Only PENDING requests govern the New Project journey. Historical
    // approved/denied rows remain visible to the Service Operator but
    // never intercept a subsequent request. Approved Productions are
    // surfaced via My Projects (the canonical discovery surface); the
    // completion / denied cards previously rendered here have been
    // retired. The backend `/production-access/status` now filters to
    // `status="pending"`; this FE reflects that server-authoritative
    // contract by deriving only the pending view.
    const pendingRequest = accessStatus?.request?.status === "pending"
        ? accessStatus.request
        : null;
    const canCreateNow = !!(accessStatus && accessStatus.can_create);
    const requestedProduction = pendingRequest?.requested_production || null;

    const submitStep1 = async (e) => {
        e.preventDefault();
        setError("");
        // Iter-158.13.2.20f.7.1 — route by ENTITLEMENT (`canCreateNow`),
        // not by beta membership. New signups have neither the beta
        // flag nor `can_create_productions=true`, but ARE gated by
        // the backend `_user_can_create_productions` check. Under the
        // pre-.20f.7.1 code these users fell into the legacy `/api/projects`
        // branch and hit a raw 403 whose enum leaked to the UI — no
        // request row was ever persisted, so the Service Operator
        // had nothing to see. Route ALL gated users into the per-
        // Production request lifecycle regardless of beta status.
        if (!canCreateNow) {
            // Advance into step 2 (destination pick) so the request
            // payload carries the full wizard state, then commit at
            // step 2.
            setStep(2);
            return;
        }
        if (hasBetaAccess) {
            // Entitled beta user — legacy per-.20f.7 direct-create branch.
            setStep(2);
            return;
        }
        // Entitled non-beta user — legacy path, unchanged.
        setLoading(true);
        try {
            const { data } = await api.post("/projects", {
                name: name.trim(), production_type: productionType,
            });
            navigate(`/projects/${data.id}`, { replace: true });
        } catch (err) {
            setError(humaniseAccessError(err));
        } finally {
            setLoading(false);
        }
    };

    // Iter-158.13.2.20f.7 (BLOCKER 1) — commit step. Runs at the end
    // of step 2 (destination pick). Branches on the caller's current
    // service-level entitlement:
    //   • can_create=true → creates the Production+project directly
    //     using the existing beta factories and advances to the
    //     step-3 setup checklist.
    //   • can_create=false → submits a per-Production access
    //     request carrying the wizard payload and advances to the
    //     step-3 lifecycle view. The Production is NOT created on
    //     the client — approval materialises it server-side using
    //     the exact same factories, with the original requester
    //     preserved as creator/initial Production Owner.
    // Iter-158.13.2.20f.7.1 (UAT correction) — for gated non-beta
    // users the flow is now identical to gated beta users: submit
    // the request; advance only after the server confirms a real
    // pending row was persisted.
    const submitStep2 = async () => {
        setError("");
        setLoading(true);
        try {
            if (canCreateNow) {
                if (hasBetaAccess) {
                    const prod = await api.post("/beta/productions", { name: name.trim() });
                    const { data } = await api.post(
                        `/beta/productions/${prod.data.id}/projects`,
                        { name: name.trim(), production_type: productionType },
                    );
                    setCreatedProjectId(data.id);
                    setStep(3);
                } else {
                    // Entitled non-beta caller — legacy /projects path.
                    const { data } = await api.post("/projects", {
                        name: name.trim(), production_type: productionType,
                    });
                    navigate(`/projects/${data.id}`, { replace: true });
                }
            } else {
                const persisted = await submitAccessRequest();
                if (persisted) setStep(3);
            }
        } catch (err) {
            setError(humaniseAccessError(err));
        } finally {
            setLoading(false);
        }
    };

    // Iter-158.13.2.20f.7 (BLOCKER 1) — when the caller lands on
    // /create-project with an existing pending request, jump straight
    // to the step-3 lifecycle view instead of asking them to
    // reconstruct the wizard payload. Grandfathered / can-create
    // callers still start at step 1.
    // Iter-158.13.2.20f.8 — Stale Approval Intercept Defect: only
    // pending requests intercept the wizard. Historical approved /
    // denied rows are no longer returned by /production-access/status
    // and never govern step routing here.
    useEffect(() => {
        if (accessLoading) return;
        if (canCreateNow) return;
        if (pendingRequest) setStep(3);
    }, [accessLoading, canCreateNow, pendingRequest]);

    return (
        <div data-testid="create-project-page" className="min-h-screen">
            <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-white/10">
                <div className="max-w-2xl mx-auto px-5 h-16 flex items-center gap-3">
                    <Link
                        data-testid="back-to-projects"
                        to="/"
                        className="tap inline-flex items-center gap-2 rounded-md px-3 h-10 text-sm font-medium text-neutral-200 hover:bg-white/5"
                    >
                        <ArrowLeft size={16} />
                        Back
                    </Link>
                    <div className="font-display font-bold text-white">
                        {step === 1 ? "New production" : step === 2 ? "Working destination" : "Setup"}
                    </div>
                </div>
            </header>

            <main className="max-w-2xl mx-auto px-5 pt-6 pb-32">
                {accessLoading ? (
                    <div data-testid="wizard-access-loading" className="text-sm text-neutral-400">
                        Loading…
                    </div>
                ) : (
                <>
                {step === 1 ? (
                    <>
                        <div className="mb-6">
                            <div
                                data-testid="wizard-step-indicator"
                                className="text-[10px] uppercase tracking-[0.3em] text-neutral-500"
                            >
                                Step 1 of {canCreateNow && !hasBetaAccess ? "1" : "3"}
                            </div>
                            <h1 className="font-display text-3xl sm:text-4xl font-black text-white mt-2">
                                Spin up a production
                            </h1>
                            <p className="text-neutral-400 text-sm mt-2">
                                Pick a name and tell us what kind of show this is.
                            </p>
                        </div>

                        {/* Iter-158.13.2.20f.7.1 — pre-release explanation
                            for gated users. Calm and lightweight — this is
                            an EXPLANATION of the next action, not a
                            warning/error state. Shown ONLY when the caller
                            is currently not entitled; grandfathered / allow-
                            listed callers see the wizard as usual. */}
                        {!canCreateNow ? (
                            <section
                                data-testid="wizard-prerelease-explainer"
                                className="mb-6 rounded-xl border border-white/10 bg-card p-4"
                            >
                                <p className="text-sm font-semibold text-neutral-100">
                                    OnSetFlow is currently in pre-release testing
                                </p>
                                <p className="text-[12px] text-neutral-400 mt-1 leading-relaxed">
                                    We&rsquo;re approving new Productions before they&rsquo;re created. Set up your Production below and send it to OnSetFlow for approval.
                                </p>
                            </section>
                        ) : null}

                        <form onSubmit={submitStep1} className="space-y-6">
                            <div className="space-y-2">
                                <Label htmlFor="proj-name" className="text-xs uppercase tracking-[0.2em] text-neutral-500">
                                    Project name
                                </Label>
                                <Input
                                    id="proj-name"
                                    data-testid="project-name-input"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="e.g. Midnight Avenue"
                                    required
                                    maxLength={120}
                                    autoFocus
                                    className="h-12 bg-secondary border-transparent focus-visible:ring-1 focus-visible:ring-primary text-base"
                                />
                            </div>

                            <fieldset className="space-y-2">
                                <legend className="text-xs uppercase tracking-[0.2em] text-neutral-500 mb-2">
                                    Production type
                                </legend>
                                <div
                                    data-testid="production-type-group"
                                    role="radiogroup"
                                    aria-label="Production type"
                                    className="grid grid-cols-1 gap-2"
                                >
                                    {PRODUCTION_TYPES.map(({ key, label, description, Icon }) => {
                                        const selected = productionType === key;
                                        return (
                                            <label
                                                key={key}
                                                data-testid={`production-type-${key}`}
                                                className={`tap relative cursor-pointer rounded-xl border p-4 flex items-center gap-3 transition-colors ${
                                                    selected
                                                        ? "border-primary/60 bg-primary/10"
                                                        : "border-white/10 bg-card hover:bg-white/5"
                                                }`}
                                            >
                                                <input
                                                    type="radio"
                                                    name="production_type"
                                                    value={key}
                                                    checked={selected}
                                                    onChange={() => setProductionType(key)}
                                                    className="sr-only"
                                                />
                                                <span
                                                    className={`h-10 w-10 shrink-0 rounded-lg flex items-center justify-center ${
                                                        selected
                                                            ? "bg-primary text-primary-foreground"
                                                            : "bg-secondary text-neutral-200"
                                                    }`}
                                                >
                                                    <Icon size={18} />
                                                </span>
                                                <span className="min-w-0 flex-1">
                                                    <span className="block text-white font-semibold leading-tight">
                                                        {label}
                                                    </span>
                                                    <span className="block text-[11px] text-neutral-400 mt-0.5">
                                                        {description}
                                                    </span>
                                                </span>
                                                <span
                                                    aria-hidden="true"
                                                    className={`h-4 w-4 shrink-0 rounded-full border-2 ${
                                                        selected
                                                            ? "border-primary bg-primary"
                                                            : "border-white/30"
                                                    }`}
                                                />
                                            </label>
                                        );
                                    })}
                                </div>
                            </fieldset>

                            {/* Iter-158.13.2.20f.7 (BLOCKER 3) — creator
                                Production Owner explanation shown BEFORE
                                the wizard commits. Four points required
                                by product: (1) every Production needs an
                                accountable Owner, (2) creating this
                                Production makes you that Owner initially,
                                (3) ownership is transferable, (4) your
                                working destination stays whatever you
                                pick next. Points 1-3 here; point 4 is
                                reinforced on the destination step.
                                Iter-158.13.2.20f.7.1 — also show to gated
                                non-beta users, since they now walk the
                                same multi-step wizard to submit a
                                per-Production request. Grandfathered
                                non-beta callers (legacy /projects flow)
                                still see the single-step wizard. */}
                            {hasBetaAccess || !canCreateNow ? (
                                <p
                                    data-testid="wizard-owner-explainer-step1"
                                    className="text-[12px] text-neutral-500 leading-relaxed"
                                >
                                    Every Production initially needs an accountable <span className="text-neutral-300">Production Owner</span>. Because you&rsquo;re starting this Production, you&rsquo;ll hold that role first — you can transfer ownership later from Production Management.
                                </p>
                            ) : null}

                            {error ? (
                                <p data-testid="create-project-error" className="text-sm text-red-400">
                                    {error}
                                </p>
                            ) : null}

                            <Button
                                data-testid="create-project-submit"
                                type="submit"
                                disabled={loading || !name.trim() || !productionType}
                                className="w-full min-tap font-semibold tracking-wide"
                            >
                                <Sparkles size={16} className="mr-2" />
                                {/* Iter-158.13.2.20f.7.1 — step-1 button
                                    label reflects the actual next action:
                                      • entitled beta → "Continue"
                                      • entitled non-beta → "Create project" (legacy single-step)
                                      • gated (any) → "Continue" — the
                                        next step is destination pick,
                                        NOT the request submission yet.
                                    "Request Production" lives on step 2
                                    where the actual request commit fires. */}
                                {loading
                                    ? (canCreateNow && !hasBetaAccess ? "Creating…" : "Continuing…")
                                    : (canCreateNow && !hasBetaAccess ? "Create project" : "Continue")}
                            </Button>
                        </form>
                    </>
                ) : null}

                {step === 2 && (hasBetaAccess || !canCreateNow) ? (
                    <>
                        <div className="mb-6">
                            <div
                                data-testid="wizard-step-indicator"
                                className="text-[10px] uppercase tracking-[0.3em] text-neutral-500"
                            >
                                Step 2 of 3
                            </div>
                            <h1 className="font-display text-3xl sm:text-4xl font-black text-white mt-2">
                                Where will you be working?
                            </h1>
                            <p className="text-neutral-400 text-sm mt-2">
                                Pick the workspace you personally live in on this production. You&rsquo;re the owner — you can always oversee every department from the Production landing.
                            </p>
                            {/* Iter-158.13.2.20f.1 (#2) — explain
                                ownership. Creating the Production makes
                                the creator its initial Production Owner
                                because canonical Script, backup and Team
                                access all need somebody responsible.
                                Ownership is transferable later.
                                Iter-158.13.2.20f.7 (BLOCKER 3, point 4)
                                — reinforce that the working destination
                                the user picks below stays their normal
                                workspace; Production Management is just
                                an additional surface they administer
                                while they own the Production. */}
                            <p
                                data-testid="wizard-owner-explainer"
                                className="text-xs text-neutral-500 mt-3 leading-relaxed"
                            >
                                Because you started this Production, you&rsquo;re its <span className="text-neutral-300">initial Production Owner</span>. That&rsquo;s the person responsible for the canonical Script, backup and who can join. You can transfer ownership later from Production Management. Your normal working destination stays the department you pick below — Production Management is just an additional surface you administer while you own the Production.
                            </p>
                        </div>

                        <div
                            data-testid="working-destination-group"
                            role="radiogroup"
                            aria-label="Working destination"
                            className="space-y-2"
                        >
                            {WORKING_DESTINATIONS.map(({ key, label, description, Icon }) => {
                                const selected = destinationKey === key;
                                return (
                                    <label
                                        key={key}
                                        data-testid={`working-destination-${key}`}
                                        className={`tap relative cursor-pointer rounded-xl border p-4 flex items-center gap-3 transition-colors ${
                                            selected
                                                ? "border-primary/60 bg-primary/10"
                                                : "border-white/10 bg-card hover:bg-white/5"
                                        }`}
                                    >
                                        <input
                                            type="radio"
                                            name="working_destination"
                                            value={key}
                                            checked={selected}
                                            onChange={() => setDestinationKey(key)}
                                            className="sr-only"
                                        />
                                        <span
                                            className={`h-10 w-10 shrink-0 rounded-lg flex items-center justify-center ${
                                                selected
                                                    ? "bg-primary text-primary-foreground"
                                                    : "bg-secondary text-neutral-200"
                                            }`}
                                        >
                                            <Icon size={18} />
                                        </span>
                                        <span className="min-w-0 flex-1">
                                            <span className="block text-white font-semibold leading-tight">
                                                {label}
                                            </span>
                                            <span className="block text-[11px] text-neutral-400 mt-0.5">
                                                {description}
                                            </span>
                                        </span>
                                        <span
                                            aria-hidden="true"
                                            className={`h-4 w-4 shrink-0 rounded-full border-2 ${
                                                selected
                                                    ? "border-primary bg-primary"
                                                    : "border-white/30"
                                            }`}
                                        />
                                    </label>
                                );
                            })}
                        </div>

                        {accessError ? (
                            <p data-testid="wizard-access-request-error" className="text-xs text-red-400 mt-4">
                                {accessError}
                            </p>
                        ) : null}
                        {error ? (
                            <p data-testid="create-project-error" className="text-sm text-red-400 mt-4">
                                {error}
                            </p>
                        ) : null}

                        {/* Iter-158.13.2.20f.7 (BLOCKER 1) — the commit
                            button label reflects the actual next action
                            so gated users understand they are submitting
                            a request for THIS specific Production, not
                            unlocking a global entitlement.
                            Iter-158.13.2.20f.7.1 — approved copy uses
                            "Request Production" — the surrounding
                            step-1 pre-release banner + step-2 explainer
                            already communicate the destination and the
                            approval semantics; the shortest truthful
                            button is the clearest. */}
                        <Button
                            data-testid={canCreateNow ? "wizard-step2-continue" : "wizard-access-request-submit"}
                            onClick={submitStep2}
                            disabled={loading || accessSubmitting || !destinationKey}
                            className="w-full min-tap font-semibold tracking-wide mt-6"
                        >
                            {canCreateNow
                                ? (loading ? "Creating…" : "Continue")
                                : (accessSubmitting || loading ? "Sending…" : "Request Production")}
                            {!(loading || accessSubmitting) ? <ArrowRight size={16} className="ml-2" /> : null}
                        </Button>

                        {!canCreateNow ? (
                            <p
                                data-testid="wizard-access-request-explainer"
                                className="text-[11px] text-neutral-500 mt-3 leading-relaxed"
                            >
                                Approval creates <em>this</em> Production, not unrestricted access. Existing invitations continue to work.
                            </p>
                        ) : null}
                    </>
                ) : null}

                {step === 3 && (hasBetaAccess || !canCreateNow) ? (
                    <>
                        {/* Iter-158.13.2.20f.7 (BLOCKER 1) — step 3 has
                            two modes:
                              (a) createdProjectId → the setup checklist
                                  (grandfathered / allowlisted callers
                                  who created the Production directly).
                              (b) pending → the request landed and is
                                  awaiting approval. Show what was
                                  requested so the user can trust it
                                  was preserved.
                            Iter-158.13.2.20f.8 — Stale Approval Intercept
                            Defect. The prior "approved" completion card
                            and "denied" resubmission card have been
                            retired. Approved Productions surface via
                            My Projects; denied requests leave no in-
                            wizard artefact so a subsequent New Project
                            attempt walks the wizard cleanly from step 1. */}
                        {createdProjectId ? (
                            <>
                                <div className="mb-6">
                                    <div
                                        data-testid="wizard-step-indicator"
                                        className="text-[10px] uppercase tracking-[0.3em] text-neutral-500"
                                    >
                                        Step 3 of 3
                                    </div>
                                    <h1 className="font-display text-3xl sm:text-4xl font-black text-white mt-2">
                                        Set up your Production
                                    </h1>
                                    <p className="text-neutral-400 text-sm mt-2">
                                        Two production-level things worth doing now. You can also come back to them from Production Management at any time.
                                    </p>
                                </div>

                                <div className="space-y-3">
                                    <div
                                        data-testid="wizard-setup-script"
                                        className="rounded-xl border border-border bg-card p-4 flex items-start gap-3"
                                    >
                                        <span className="h-10 w-10 shrink-0 rounded-lg bg-primary/20 text-primary flex items-center justify-center">
                                            <FileText size={18} />
                                        </span>
                                        <div className="min-w-0 flex-1">
                                            <div className="text-white font-semibold">Canonical script</div>
                                            <p className="text-[12px] text-neutral-400 mt-1">
                                                Upload or paste your script. Scenes and characters become the shared spine of every department.
                                            </p>
                                            <Link
                                                data-testid="wizard-setup-script-open"
                                                to={`/beta/projects/${createdProjectId}/script-setup`}
                                                className="tap mt-3 inline-flex items-center gap-2 px-3 h-9 rounded-full text-[12px] uppercase tracking-[0.2em] bg-primary text-primary-foreground hover:opacity-90"
                                            >
                                                Open script setup
                                                <ArrowRight size={14} />
                                            </Link>
                                        </div>
                                    </div>

                                    <div
                                        data-testid="wizard-setup-backup"
                                        className="rounded-xl border border-border bg-card p-4 flex items-start gap-3"
                                    >
                                        <span className="h-10 w-10 shrink-0 rounded-lg bg-emerald-500/20 text-emerald-200 flex items-center justify-center">
                                            <ShieldCheck size={18} />
                                        </span>
                                        <div className="min-w-0 flex-1">
                                            <div className="text-white font-semibold">Back Up Your Production</div>
                                            <p className="text-[12px] text-neutral-400 mt-1">
                                                Connect Dropbox (or another provider) so every file you upload is mirrored to your own storage. You can add or change providers later.
                                            </p>
                                            <Link
                                                data-testid="wizard-setup-backup-open"
                                                to={`/beta/projects/${createdProjectId}/backups`}
                                                className="tap mt-3 inline-flex items-center gap-2 px-3 h-9 rounded-full text-[12px] uppercase tracking-[0.2em] bg-emerald-500/20 text-emerald-100 border border-emerald-500/30 hover:bg-emerald-500/30"
                                            >
                                                Set Up Backup
                                                <ArrowRight size={14} />
                                            </Link>
                                        </div>
                                    </div>
                                </div>

                                <Button
                                    data-testid="wizard-finish"
                                    onClick={() => navigate(finalLanding, { replace: true })}
                                    className="w-full min-tap font-semibold tracking-wide mt-6"
                                >
                                    <CheckCircle2 size={16} className="mr-2" />
                                    Take me to {chosenDestination.label}
                                </Button>
                                <p className="text-[11px] text-neutral-500 mt-3 text-center">
                                    You can revisit script setup and backup any time from the Production Overview.
                                </p>
                            </>
                        ) : pendingRequest ? (
                            <section data-testid="wizard-access-request-pending" className="space-y-4">
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-300">
                                        Sent
                                    </div>
                                    {/* Iter-158.13.2.20f.7.1 — success
                                        confirmation displayed only after
                                        the server confirms a real
                                        persisted pending row. Copy
                                        deliberately does NOT promise an
                                        email notification (transactional
                                        email is not operational in this
                                        release). */}
                                    <h1
                                        data-testid="wizard-access-request-sent-headline"
                                        className="font-display text-3xl sm:text-4xl font-black text-white mt-2"
                                    >
                                        Production request sent
                                    </h1>
                                    <p className="text-neutral-400 text-sm mt-2 leading-relaxed max-w-prose">
                                        Your Production has been sent to OnSetFlow for approval. You&rsquo;ll be able to continue once it has been approved.
                                    </p>
                                </div>
                                <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4 flex items-start gap-3">
                                    <Clock size={16} className="text-amber-300 shrink-0 mt-0.5" />
                                    <div className="min-w-0 flex-1">
                                        <p className="text-sm font-semibold text-amber-100">
                                            {requestedProduction?.name || "Your Production"}
                                        </p>
                                        {requestedProduction ? (
                                            <p
                                                data-testid="wizard-access-request-pending-summary"
                                                className="text-[12px] text-amber-100/80 mt-1"
                                            >
                                                {PRODUCTION_TYPES.find((t) => t.key === requestedProduction.production_type)?.label
                                                    || requestedProduction.production_type}
                                                {requestedProduction.destination_key
                                                    ? ` · ${(WORKING_DESTINATIONS.find((d) => d.key === requestedProduction.destination_key) || {}).label || requestedProduction.destination_key}`
                                                    : ""}
                                            </p>
                                        ) : null}
                                        <p className="text-[12px] text-amber-100/80 mt-2 leading-relaxed">
                                            Submitted {pendingRequest.requested_at?.slice(0, 16).replace("T", " ")}. Check back on this page — your access status updates as soon as our team approves the request.
                                        </p>
                                    </div>
                                </div>
                                <p className="text-[11px] text-neutral-500 leading-relaxed">
                                    This is a service-level entitlement — not a Production role. Existing Owners/Admins/Department roles are unaffected.
                                </p>
                            </section>
                        ) : null}
                    </>
                ) : null}
                </>
                )}
            </main>
        </div>
    );
}
